import axios from 'axios';

// Dev: http://localhost:8001  |  Docker: '' (relative URL → nginx/Traefik proxy)
// REACT_APP_API_URL="" (empty string) in Docker → relative URLs; undefined in dev → fallback
const BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:8001';

const api = axios.create({ baseURL: BASE, timeout: 30000 });

// ── Request interceptor: attach JWT ───────────────────────────────────────
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // 🚨 НЕ БРАТИ ВІДПОВІДІ API З КЕШУ БРАУЗЕРА. У мить перезбірки бекенду
  // проксі віддає СТОРІНКУ кабінету з кодом 200 та ETag — і браузер має право
  // її закешувати. Тоді вкладка вічно бере HTML із кешу, хоча сервер давно
  // справний, а звичайний F5 не лікує: з ETag він отримує 304 і той самий
  // HTML. Спіймано 07.09: в одному браузері кабінет живий, в іншому — «замість
  // даних повернулась сторінка», і рятував лише Ctrl+Shift+R.
  // Заборона стоїть з ОБОХ боків: сервер шле no-store, клієнт не питає кеш.
  config.headers['Cache-Control'] = 'no-cache';
  return config;
});

// ── 🚨 HTML ЗАМІСТЬ JSON — ЦЕ ПОМИЛКА, А НЕ ДАНІ ──────────────────────────
// Коли бекенд недоступний (перезбірка, рестарт, обрив), проксі віддає сторінку
// кабінету з кодом 200. Axios кладе в `res.data` РЯДОК, а код на місці виклику
// робить `res.data || []` — і непорожній рядок проходить як «дані». Далі
// перший же `.filter` кидає «is not a function», React демонтується, і в
// браузері лишається ЧОРНИЙ ЕКРАН з однією загадковою помилкою.
// Спіймано двічі за день: у кабінеті (/analyzer) і в агента парку.
// ⚠️ ПОВІДОМЛЕННЯ МУСИТЬ НАЗИВАТИ ЗАПИТ І КОД. Перша версія казала лише
// «замість даних повернулась сторінка» — і коли 04.09 це вилізло в одному
// браузері з двох, з'ясувати причину з самого повідомлення було НЕМОЖЛИВО:
// невідомо ні який запит, ні що відповів сервер. А різниця велика: 200 зі
// сторінкою — це стара вкладка чи проксі повз бекенд, 502/503 — бекенд саме
// перезбирається, і чекати треба різного.
function htmlInsteadOfJson(response) {
  const err = new Error(
    `Сервер недоступний: замість даних повернулась сторінка `
    + `(${response.config?.url || '?'} → код ${response.status}). `
    + `Якщо це повторюється — оновіть сторінку з Ctrl+Shift+R: вкладка могла `
    + `лишитись зі старою версією кабінету.`);
  err.isProxyHtml = true;
  err.config = response.config;
  err.status = response.status;
  return Promise.reject(err);
}

api.interceptors.response.use(response => {
  const ct = String(response.headers?.['content-type'] || '');
  if (typeof response.data === 'string' && /^\s*<(!doctype|html)/i.test(response.data)) {
    return htmlInsteadOfJson(response);
  }
  if (ct.includes('text/html') && response.config?.url?.startsWith('/api')) {
    return htmlInsteadOfJson(response);
  }
  return response;
});

// ── Response interceptor: 401 → logout event ─────────────────────────────
api.interceptors.response.use(
  response => response,
  error => {
    // Only auto-logout on 401 from protected endpoints (not from /auth/ itself)
    if (error.response?.status === 401 && !error.config.url.includes('/auth/')) {
      localStorage.removeItem('token');
      localStorage.removeItem('username');
      localStorage.removeItem('avatar');
      window.dispatchEvent(new Event('auth:logout'));
    }
    return Promise.reject(error);
  }
);

// ── Auth ──────────────────────────────────────────────────────────────────
export const register    = (body) => api.post('/api/auth/register', body);
export const login       = (body) => api.post('/api/auth/login',    body);
export const googleLogin = (body) => api.post('/api/auth/google',   body);
export const forgotPassword = (body) => api.post('/api/auth/forgot-password', body);
export const resetPassword  = (body) => api.post('/api/auth/reset-password',  body);
export const getMe       = ()     => api.get('/api/auth/me');

// Маржинальність всесвіту: чи дає IBKR кредит під кожен папір (сторінка /marginality)
export const getMarginality = () => api.get('/api/marginality');
// Сторінка «Прогнози» (лише адмін): вкладка «Аналіз», timeframe '1h' | '1d'.
export const getForecastAnalysis = (timeframe) => api.get('/api/forecasts/analysis', { params: { timeframe } });
// Вкладка «Акції»: зріз на дату (YYYY-MM-DD, null = остання) і годину закриття бару ET (10…16).
// Вкладка «Акція»: один папір, timeframe '1h' | '1d', момент — дата + година закриття бару ET.
export const getForecastStock = (ticker, timeframe, date, hour) => api.get(`/api/forecasts/stock/${encodeURIComponent(ticker)}`, { params: { timeframe, ...(date ? { date, hour } : {}) } });
export const getForecastScreen = (date, hour) => api.get('/api/forecasts/screen', { params: { ...(date ? { date } : {}), hour } });

// ── Машини парку (сторінка /workers, лише адмін) ──────────────────────────
// Ходить під ЗВИЧАЙНИМ токеном кабінету. Агентський спільний секрет лежить на
// чужих машинах і в браузер не потрапляє ніколи.
export const getWorkers      = ()                 => api.get('/api/admin/workers');
export const getParkSpeed    = ()                 => api.get('/api/admin/workers/speed');
export const setWorkerPaused = (agentId, paused)  =>
  api.post(`/api/admin/workers/${encodeURIComponent(agentId)}/pause`, { paused });
// Налаштування машин (повзунок, «Робота», пауза при людині, розклад) — лише
// розділи, які змінили; одна правка на кілька машин.
export const setWorkersSettings = (agentIds, patch) =>
  api.post('/api/admin/workers/settings', { agent_ids: agentIds, patch });
// Перезапуск агента / перезавантаження / вимкнення машин — лише там, де людина
// за машиною дозволила це у вікні агента (агент 70+).
export const getWorkerFailures = (agentId)        =>
  api.get(`/api/admin/workers/${encodeURIComponent(agentId)}/failures`);
export const sendWorkersCommand = (agentIds, action) =>
  api.post('/api/admin/workers/command', { agent_ids: agentIds, action });
export const forgetWorker    = (agentId)          =>
  api.delete(`/api/admin/workers/${encodeURIComponent(agentId)}`);

// Токени машин. З 19.10 список несе `token_plain` (для перевстановлення
// агента); видані раніше — лише хеш, без тексту.
export const getAgentTokens  = ()                 => api.get('/api/admin/agent-tokens');
export const issueAgentToken = (label)            =>
  api.post('/api/admin/agent-tokens', { label });
export const revokeAgentToken = (id, revoked)     =>
  api.post(`/api/admin/agent-tokens/${encodeURIComponent(id)}/revoke`, { paused: revoked });
export const unbindAgentToken = (id)              =>
  api.post(`/api/admin/agent-tokens/${encodeURIComponent(id)}/unbind`);
export const deleteAgentToken = (id)              =>
  api.delete(`/api/admin/agent-tokens/${encodeURIComponent(id)}`);

// Пакети агента. Файл качається під СЕСІЄЮ кабінету (responseType blob), а не
// посиланням із токеном в адресі: токен в URL осідає в історії браузера та в
// логах проксі.
export const getAgentDownloads = ()               => api.get('/api/admin/agent-downloads');
export const fetchAgentPackage = (name)           =>
  api.get(`/api/admin/agent-downloads/${encodeURIComponent(name)}`,
          { responseType: 'blob' });
// Перепустка на завантаження: браузер качає файл САМ, зі смужкою прогресу й
// одразу на диск. Через blob вище третина гігабайта їхала в памʼять мовчки —
// людина бачила «натиснув і нічого не відбувається» хвилин пʼять.
export const createAgentDownloadTicket = (name)   =>
  api.post('/api/admin/agent-download-ticket', { name });

// Перегляд «Кластера» за паролем: налаштування (лише адмін).
export const getClusterShare = ()                 => api.get('/api/admin/cluster-share');
export const putClusterShare = (enabled, password) =>
  api.put('/api/admin/cluster-share', { enabled, password });

// ── Глядач «Кластера» (системний адміністратор парку, без облікового запису) ──
// 🚨 ОКРЕМИЙ КЛІЄНТ, а не `api`: перехоплювач `api` на будь-який 401 розлогінює
// кабінет. Глядач отримує 401, коли адмін змінив пароль, — і це має вести на
// форму пароля, а не стирати чужу сесію кабінету в тому ж браузері.
export const CLUSTER_VIEW_KEY = 'cluster_view_token';
const viewApi = axios.create({ baseURL: BASE, timeout: 30000 });
viewApi.interceptors.request.use(config => {
  let tok = null;
  try { tok = localStorage.getItem(CLUSTER_VIEW_KEY); } catch (e) { /* приватне вікно */ }
  if (tok) config.headers['X-Cluster-Token'] = tok;
  config.headers['Cache-Control'] = 'no-cache';
  return config;
});
viewApi.interceptors.response.use(response => {
  if (typeof response.data === 'string' && /^\s*<(!doctype|html)/i.test(response.data)) {
    return htmlInsteadOfJson(response);
  }
  return response;
});
export const clusterView = {
  status:          ()      => viewApi.get('/api/cluster-view/status'),
  login:           (password) => viewApi.post('/api/cluster-view/login', { password }),
  getWorkers:      ()      => viewApi.get('/api/cluster-view/workers'),
  getAgentTokens:  ()      => viewApi.get('/api/cluster-view/agent-tokens'),
  issueAgentToken: (label) => viewApi.post('/api/cluster-view/agent-tokens', { label }),
  getAgentDownloads: ()    => viewApi.get('/api/cluster-view/agent-downloads'),
  createAgentDownloadTicket: (name) =>
    viewApi.post('/api/cluster-view/download-ticket', { name }),
};

// ── Bot & Data ────────────────────────────────────────────────────────────
export const getStatus      = (cfg)      => api.get('/api/status',       cfg);
export const getPortfolio   = (cfg)      => api.get('/api/portfolio',     cfg);
export const getStats       = (cfg)      => api.get('/api/stats',         cfg);
export const getEquityCurve = (cfg)      => api.get('/api/equity-curve',  cfg);
export const getScreener    = (refresh = false) => api.get(`/api/screener${refresh ? '?refresh=true' : ''}`);
export const getStrategies  = ()         => api.get('/api/strategies');
export const getTrades      = (params)   => api.get('/api/trades', { params });
export const getReportSettings    = ()     => api.get('/api/report-settings');
export const updateReportSettings = (body) => api.put('/api/report-settings', body);
export const saveStrategy   = (body)     => api.post('/api/strategy', body);
export const deleteStrategy = (key)      => api.delete(`/api/strategy/${key}`);
export const startBot             = (body) => api.post('/api/bot/start', body);
export const stopBot              = ()     => api.post('/api/bot/stop');
export const getMultiBotStatus    = ()     => api.get('/api/bot/multi-status');
export const addStrategyToBot      = (body) => api.post('/api/bot/add-strategy', body);
export const removeStrategyFromBot = (name) => api.post('/api/bot/remove-strategy', { name });
export const pauseStrategy         = (name) => api.post('/api/bot/pause-strategy',  { name });
export const resumeStrategy        = (name) => api.post('/api/bot/resume-strategy', { name });
export const resetStrategy         = (name) => api.post('/api/bot/reset-strategy', { name });
export const getRunningStatus      = ()     => api.get('/api/bot/running-status');
export const getRunningNames       = ()     => api.get('/api/bot/running-names');
export const getBotDiagnostics     = ()     => api.get('/api/bot/diagnostics');
export const getBotLiveDetail      = (name) => api.get('/api/bot/live-detail', { params: name ? { name } : {} });
export const getBotScreening       = ()     => api.get('/api/user/screening');

// ── Active strategy (public) ───────────────────────────
export const getActiveStrategyPublic = () => api.get('/api/active-strategy');

// ── Data catalog ──────────────────────────────────────────────────────────
export const getDataTickers  = (params) => api.get('/api/data/tickers', { params });
export const getDataSummary  = () => api.get('/api/data/summary');
export const refreshDataCache = () => api.post('/api/data/refresh-cache');
export const getAvailableTickers = (dataType = 'hourly') =>
  api.get('/api/analyzer/available-tickers', { params: { data_type: dataType } });
export const getUniverseCount = (body, cfg = {}) =>
  api.post('/api/analyzer/universe-count', body, cfg);
export const getTickerLists = () => api.get('/api/analyzer/ticker-lists');
// Межа покриття делістингових + кількість подій. Береться з даних на
// сервері, бо зашитий у форму рядок застаряє з кожною новою закачкою.
export const getDelistingCoverage = () => api.get('/api/analyzer/delisting-coverage');
export const getDataCatalog  = () => api.get('/api/data/catalog');
export const getTickersList  = () => api.get('/api/tickers/list');
export const downloadMinuteData = (interval = '5m', limit = 100) =>
  api.post('/api/data/download-minute', { interval, limit });
export const getDataSectors       = () => api.get('/api/data/sectors');
export const getDataMarketStats   = () => api.get('/api/data/market-stats');
export const getHourlyProgress    = () => api.get('/api/data/hourly-progress');
export const getTickerCompanyInfo = (ticker) => api.get(`/api/data/tickers/${ticker}/company-info`);
export const getTickerOHLCV = (ticker, type = 'daily', from = null, to = null) => {
  const params = { type };
  if (from) params.from = from;
  if (to) params.to = to;
  return api.get(`/api/data/tickers/${ticker}/ohlcv`, { params });
};

// ── Analyzer ──────────────────────────────────────────────────────────────
export const getAnalyzerSearches  = ()       => api.get('/api/analyzer/searches');
export const saveAnalyzerSearch   = (body)   => api.post('/api/analyzer/searches', body);
export const deleteAnalyzerSearch = (id)     => api.delete(`/api/analyzer/searches/${id}`);
export const updateAnalyzerSearchComment = (id, comment) => api.patch(`/api/analyzer/searches/${id}/comment`, { comment });
export const runAnalyzer          = (body)   => api.post('/api/analyzer/run', body);
export const getAnalyzerProgress  = (batchId) => api.get(`/api/analyzer/progress/${batchId}`);
export const cancelAnalyzerBatch  = (batchId) => api.post(`/api/analyzer/cancel/${batchId}`);
export const getAnalyzerBatches   = ()       => api.get('/api/analyzer/batches');
export const getAnalyzerResults   = (params) => api.get('/api/analyzer/results', { params });
export const getAnalyzerResult    = (id)     => api.get(`/api/analyzer/results/${id}`);
// Вікна одного рядка. Список їх не возить (55 КБ на рядок × 1000 рядків),
// тож попап «📊 N» бере їх сам у момент відкриття.
export const getAnalyzerResultPeriods = (id) =>
  api.get(`/api/analyzer/results/${id}/periods`);
export const deleteAnalyzerResult = (id)      => api.delete(`/api/analyzer/results/${id}`);
export const saveAnalyzerResult   = (id)      => api.post(`/api/analyzer/results/${id}/save`);
export const unsaveAnalyzerResult = (id)      => api.delete(`/api/analyzer/results/${id}/save`);
// Звірка зірочок: список живе в налаштуваннях користувача, рядки — у базі, і
// вони розʼїжджаються мовчки (27.08: у чипі 82, у базі існувало 6).
export const syncStarredIds        = (ids)     =>
  api.post('/api/analyzer/results/starred/sync', { ids });
// Єдине джерело зірочок — is_saved у рядках (08.10).
export const getStarredIds         = ()        => api.get('/api/analyzer/results/starred');
export const deleteAnalyzerBulk     = (ids)    => api.delete('/api/analyzer/results/bulk', { data: { ids } });
export const deleteAnalyzerFiltered = (params) => api.delete('/api/analyzer/results/filtered', { params });
export const deleteAnalyzerBatch        = (batchId) => api.delete(`/api/analyzer/batch/${batchId}`);
export const recalculateAnalyzerResult  = (id)      => api.post(`/api/analyzer/recalculate/${id}`);
// Sync period sims can take 10-30s each + price load — match backend timeout (180s)
// з невеликим запасом (200s) щоб клієнт не обірвав до server timeout.
export const recalcPeriodDetail         = (rowId, periodIdx) =>
  // 300 с > 240 с бекенда: фронтенд має чекати ДОВШЕ, інакше користувач бачить
  // сирий обрив axios замість пояснення з 408. Порядок стель важливіший за самі
  // числа. Виміряно: задача періоду ~104 с, воркер однозадачний (MAX_JOBS=1).
  api.post(`/api/analyzer/results/${rowId}/period-detail/${periodIdx}`, null, { timeout: 300000 });
// Генератор перевірочних вікон для форми пошуку. Рахує сервер — там єдина
// формула (спільна з перевіркою стійкості) і там відомий останній бар сховища,
// яким треба обрізати праву межу.
export const generateAnalyzerPeriods    = (body) =>
  api.post('/api/analyzer/generate-periods', body, { timeout: 30000 });
// Перевірка відзначених рядків на новому наборі вікон. Повертається ОДРАЗУ:
// рахує звичайна розподілена сітка, а вікна дописуються в рядки вже воркером.
export const recheckSelectedRows        = (body) =>
  api.post('/api/analyzer/results/recheck', body, { timeout: 60000 });
// Прикріпити/відкріпити блок дописаних вікон від підсумку рядка.
export const togglePeriodBlock          = (rowId, blockId, included) =>
  api.post(`/api/analyzer/results/${rowId}/period-blocks/${encodeURIComponent(blockId)}`,
           { included });
export const appendPeriods              = (rowId, periods) =>
  api.post(`/api/analyzer/results/${rowId}/append-periods`, { periods }, { timeout: 200000 });
// «+» на рядку таблиці періодів: той самий початок, вікно довше на N місяців.
// Одна симуляція, але вікно може виходити за головний період рядка — тоді воно
// вантажить власні дані, тож стеля щедра.
export const extendPeriod               = (rowId, start, months) =>
  api.post(`/api/analyzer/results/${rowId}/extend-period`, { start, months },
           { timeout: 360000 });
// Драбина варіантів: та сама стратегія на діапазоні значень однієї осі.
// Повертається ОДРАЗУ — рахує звичайна сітка, щаблі записує воркер.
export const runVariantLadder           = (rowId, body) =>
  api.post(`/api/analyzer/results/${rowId}/variants`, body, { timeout: 60000 });
// Щаблі драбини, які вже пораховані. Ендпоінт з\'явиться разом із наступним
// викочуванням бекенда — до того фронт мовчки обходиться лічильником прогресу.
// Драбина, що рахується для рядка зараз (щоб побачити її після перезавантаження).
export const getActiveVariant           = (rowId) =>
  api.get(`/api/analyzer/results/${rowId}/variants/active`);
export const getVariantProgress         = (rowId, batchId) =>
  api.get(`/api/analyzer/results/${rowId}/variants/progress/${batchId}`);
export const recalcAllPeriods           = (rowId) =>
  api.post(`/api/analyzer/results/${rowId}/recalc-all-periods`, null, { timeout: 200000 });
// R6: перевірка стійкості. Запуск ЛИШЕ СТАВИТЬ ЗАДАЧУ В ЧЕРГУ — рахує її
// окремий процес на сервері (20 випадкових вікон із завантаженням даних це
// десятки хвилин). Прогрес опитується окремо.
// ⚠️ Раніше цей POST теж падав на таймаут, хоч сам нічого не рахує: перевірка
// крутилась у процесі, який відповідає на запити, і глушила його цілком.
export const checkStability             = (rowId, settings) =>
  api.post(`/api/analyzer/results/${rowId}/stability`, settings || {}, { timeout: 120000 });
export const getStabilityProgress       = (rowId) =>
  api.get(`/api/analyzer/results/${rowId}/stability/progress`, { timeout: 60000 });
// «+» на рядку: той самий початок, але вікно довше на N місяців. Одне вікно,
// але з холодним кешем ще й завантаження даних — звідси щедрий таймаут.
export const extendStabilityPeriod      = (rowId, start, months) =>
  api.post(`/api/analyzer/results/${rowId}/stability/extend`, { start, months },
           { timeout: 900000 });
// Драбина умов (плече / мін. обіг) на ОДНОМУ рядку: ті самі 16 значень у формі
// пошуку дали б ×16 до всієї сітки, а тут це 16 (для плеча — 32) симуляцій.
export const checkMarginLadder          = (rowId, body) =>
  api.post(`/api/analyzer/results/${rowId}/margin-ladder`, body, { timeout: 1800000 });
export const recalculateAnalyzerBatch         = (ids) => api.post('/api/analyzer/recalculate-batch', { ids });
export const getRecalculateBatchProgress      = ()    => api.get('/api/analyzer/recalculate-batch/progress');
export const recalculateAnalyzerAll     = ()         => api.post('/api/analyzer/recalculate-all');
export const cancelRecalculateAll       = ()         => api.post('/api/analyzer/recalculate-all/cancel');
export const getRecalculateProgress     = ()         => api.get('/api/analyzer/recalculate-progress');
export const getAnalyzerRegimes         = (dataType) => api.get('/api/analyzer/regimes', { params: { data_type: dataType } });
export const recalculateRegimes         = ()         => api.post('/api/analyzer/regimes/recalculate');

// ── Analyzer Advanced ─────────────────────────────────────────────────────────
export const runAnalyzerAdvanced          = (body)    => api.post('/api/analyzer/advanced/run', body);
// Перебір по готовому блоку: сітки-кандидати + лічильник до запуску (нічого не пише).
export const getAnalyzerBlocks            = ()        => api.get('/api/analyzer/blocks');
export const previewAnalyzerBlock         = (body)    => api.post('/api/analyzer/blocks/preview', body);
export const getAnalyzerAdvancedResults   = (params)  => api.get('/api/analyzer/advanced/results', { params });
export const getAnalyzerAdvancedProgress  = (batchId) => api.get(`/api/analyzer/advanced/progress/${batchId}`);

// ── User Settings ──────────────────────────────────────────────────────────
export const getUserSettings  = ()     => api.get('/api/user/settings');
export const saveUserSettings = (body) => api.put('/api/user/settings', body);

// ── User Profile & IBKR Credentials ────────────────────────────────────────
export const getUserProfile          = ()     => api.get('/api/user/profile');
export const getBrokerCredentials    = (cfg)  => api.get('/api/user/broker-credentials', cfg);
export const saveBrokerCredentials   = (body) => api.post('/api/user/broker-credentials', body);
export const deleteBrokerCredentials = ()     => api.delete('/api/user/broker-credentials');
export const setTradingMode          = (body) => api.post('/api/user/trading-mode', body);
export const getBalanceHistory       = (days) => api.get('/api/user/balance-history', { params: { days } });

// ── Analytics ──────────────────────────────────────────────────────────────
export const getMarketVolatility = (params, config = {}) =>
  api.get('/api/analytics/market-volatility', { params, ...config });

export const getSPY = (params, config = {}) =>
  api.get('/api/analytics/spy', { params, ...config });

// ── Live-bot push targets (admin) ──────────────────────────────────────────
// Which live bot a strategy is pushed to. Targets come from backend env
// (TRADING_TARGETS); the chosen one is persisted globally in localStorage so
// every push button (both analyzers + detail) honours the same selection.
export const LS_PUSH_TARGET = 'push_trading_target';
export const getTradingTargets   = ()           => api.get('/api/admin/trading-targets');
export const pushResultToTrading = (id, target) =>
  api.post(`/api/admin/analyzer-results/${id}/push-to-trading`, null,
           { params: target ? { target } : {} });

// ── Public landing calculator (no auth) ────────────────────────────────────
// Backtests the active global strategy over [start_date, end_date] for `amount`.
// Heavy on a cold cache → generous timeout.
export const landingBacktest = (body) =>
  api.post('/api/public/landing-backtest', body, { timeout: 120000 });

// ── Public contact form (no auth) ──────────────────────────────────────────
// body: { name, phone, email, text, files: File[] } — sent as multipart/form-data
// so attachments go straight through; axios sets the boundary header itself.
export const submitContact = ({ name, phone, email, text, files }) => {
  const fd = new FormData();
  fd.append('name', name);
  fd.append('phone', phone);
  fd.append('email', email || '');
  fd.append('text', text);
  (files || []).forEach((f) => fd.append('files', f));
  return api.post('/api/public/contact', fd, { timeout: 60000 });
};

// ── Virtual trading sessions (authenticated) ───────────────────────────────
// Перший запуск за день вантажить ціни на сервері — виміряно 54.5 с (13.10),
// тож дефолтні 30 с обривали запит, і людина бачила «Помилка запуску», хоча
// сесія створювалась. GET теж може перераховувати (кеш старший за годину).
export const createVirtualSession = (body) => api.post('/api/virtual/session', body, { timeout: 180000 });
export const getVirtualSession    = ()     => api.get('/api/virtual/session', { timeout: 180000 });
export const deleteVirtualSession = ()     => api.delete('/api/virtual/session');
export const getVirtualStatus     = ()     => api.get('/api/virtual/status');

// Default export for direct use in components
export default api;
